'use client'
import {
  OrderBoxAssetSelect,
  PaymentType,
  mapProjectToFlatAsset,
} from '@repo/shared/components'
import { useProject } from '@repo/shared/hooks'
import { useRouter } from '@repo/shared/i18n/navigation'
import { UserProject, useListProjects, useUserPortfolio } from '@repo/services'
import {
  Card,
  Col,
  Icon,
  PriceChangeBadge,
  Row,
  Typography,
} from '@repo/ui/components'
import { useSearchParams } from 'next/navigation'
import { useMemo, useState } from 'react'
import { type FlatAsset } from '@repo/shared/components'

interface AdvancedTradeHeaderProps {
  projectData?: UserProject & {
    totalSupply: number
  }
}

export const AdvancedTradeHeader = (props: AdvancedTradeHeaderProps) => {
  const { projectData } = props
  const searchParams = useSearchParams()
  const { replace } = useRouter()
  const [searchQuery, setSearchQuery] = useState('')

  // List projects hook for asset select
  const listProjectsData = useListProjects({
    skip: 0,
    limit: 10,
    // provinceID: 123,
    tradeStatus: 'active',
    kind: 'property',
    ...(searchQuery && {
      q: searchQuery,
    }),
  })

  const tokenFromUrl = useMemo(() => {
    return searchParams.get('token') || undefined
  }, [searchParams])

  const { project } = useProject(tokenFromUrl)

  // Get user portfolio data
  const { data: { data = {} } = {} } = useUserPortfolio()

  const selectedAsset = useMemo(() => {
    if (project) {
      return project as unknown as FlatAsset
    }
    if (tokenFromUrl) {
      return { token: tokenFromUrl } as FlatAsset
    }
    return undefined
  }, [project, tokenFromUrl])

  const setSelectedAsset = (asset: FlatAsset | undefined) => {
    const params = new URLSearchParams(searchParams.toString())

    if (asset?.token) {
      params.set('token', asset.token)
    } else {
      params.delete('token')
    }

    replace(`/advanced-trade?${params.toString()}`)
  }

  // Create allProjects from user portfolio data
  const allProjects = useMemo(() => {
    const projects = data?.projects || []
    return projects
      .filter((project) => project.kind === 'property')
      .map((project) => mapProjectToFlatAsset(project))
  }, [data?.projects])

  // Extract price data from projectData (original API data)
  const currentPrice =
    (projectData?.price?.priceRange24h?.last || 0) > 0
      ? projectData?.price?.priceRange24h?.last
      : undefined
  const priceChange = projectData?.price?.priceRange24h?.deltaPercent || 0
  const highestPrice24h =
    (projectData?.price?.priceRange24h?.last || 0) > 0
      ? projectData?.price?.priceRange24h?.last || 0
      : undefined
  const lowestPrice24h = projectData?.price?.priceRange24h?.first

  return (
    <Row className='py-md gap-3xl justify-between'>
      <div className='min-w-[360px] max-w-[360px] px-md'>
        <OrderBoxAssetSelect
          containerClassName='bg-neutral-surface-5 max-h-5xl'
          tokenClassName='md:max-w-[165px]'
          iconClassName='min-w-6 max-w-6 min-h-6 max-h-6'
          selectedAsset={selectedAsset}
          onChange={(e) => {
            setSelectedAsset(e)
          }}
          projects={allProjects}
          onSearchChange={setSearchQuery}
          tab='buy'
          listProjectsData={listProjectsData as any}
        />
      </div>
      <Row className='justify-between'>
        <Row className='gap-xl w-fit'>
          <Col className='gap-xs'>
            <Typography
              size='2xs'
              color='text-neutral-surface-60'
              className='whitespace-nowrap'
            >
              آخرین قیمت
            </Typography>
            <Row className='gap-xs'>
              <PaymentType value={currentPrice} size='2xs' numberSize='sm' />
              <PriceChangeBadge
                value={priceChange}
                className='bg-transparent mt-xs !p-0'
              />
            </Row>
          </Col>
          <Col className='gap-xs'>
            <Typography
              size='2xs'
              color='text-neutral-surface-60'
              className='whitespace-nowrap'
            >
              بیش‌ترین قیمت در ۲۴ ساعت
            </Typography>
            <Row className='gap-xs'>
              <PaymentType value={highestPrice24h} size='2xs' numberSize='sm' />
            </Row>
          </Col>
          <Col className='gap-xs'>
            <Typography
              size='2xs'
              color='text-neutral-surface-60'
              className='whitespace-nowrap'
            >
              کم‌ترین قیمت در ۲۴ ساعت
            </Typography>
            <Row className='gap-xs'>
              <PaymentType value={lowestPrice24h} size='2xs' numberSize='sm' />
            </Row>
          </Col>
        </Row>

        <Card className='p-xs bg-neutral-surface-5 rounded-sm ml-md'>
          <Icon name='InfoCircleGhost1' className='w-6 h-6' />
        </Card>
      </Row>
    </Row>
  )
}
